import { Col } from 'antd';
import { useField } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useListBanksQuery } from '../../../api/queries/listBanks';
import { BankSortingField, SortingOrder } from '../../../api/types';
import FormFields from '../../../themes/admin/Fields/FormFields';
import useTranslatedString from '../../../utilities/useTranslatedString';

const colSpan = { lg: 8, md: 12, xs: 24 };

const useMarketTypeField = (countryCode, { coeName, ppsrName, estFeeName }) => {
    // handle coe value
    const isSingapore = countryCode === 'SG';
    const [, , { setValue }] = useField(coeName);
    useEffect(() => {
        if (!isSingapore) {
            setValue(0);
        }
    }, [isSingapore, setValue]);

    // handle ppsr/estFee value
    const isNewZealand = countryCode === 'NZ';
    const [, , { setValue: setPPSRValue }] = useField(ppsrName);
    const [, , { setValue: setEstFeeValue }] = useField(estFeeName);
    useEffect(() => {
        if (!isNewZealand) {
            setPPSRValue(0);
            setEstFeeValue(0);
        }
    }, [isNewZealand, setPPSRValue, setEstFeeValue]);

    return { isSingapore, isNewZealand };
};

interface MarketTypeFieldProps {
    companyId?: string;
    countryCode?: string;
    prefix?: string;
    disabled?: boolean;
}

type BankEstFeeDisplayFieldsProps = {
    companyId: string;
};

const BankEstFeeDisplayFields = ({ companyId }: BankEstFeeDisplayFieldsProps): JSX.Element => {
    const { t } = useTranslation(['moduleDetails']);
    const translate = useTranslatedString();
    const { data } = useListBanksQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            sort: {
                field: BankSortingField.Order,
                order: SortingOrder.Asc,
            },
            filter: { companyId },
        },
        skip: !companyId,
    });

    const banks = useMemo(() => data?.list?.items ?? [], [data?.list?.items]);

    return (
        <>
            {banks.map(bank => (
                <Col {...colSpan} key={bank.id}>
                    <FormFields.DisplayField
                        {...t('moduleDetails:fields.bankEstFee', {
                            returnObjects: true,
                            bankLegalName: translate(bank.legalName),
                        })}
                        value={bank.estFee}
                    />
                </Col>
            ))}
        </>
    );
};

const MarketTypeField = ({ disabled = false, countryCode, prefix = '', companyId }: MarketTypeFieldProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const finalPrefix = prefix ? `${prefix}.` : '';
    const coeName = `${finalPrefix}coe`;
    const ppsrName = `${finalPrefix}ppsr`;
    const estFeeName = `${finalPrefix}estFee`;

    const { isSingapore, isNewZealand } = useMarketTypeField(countryCode, { coeName, ppsrName, estFeeName });

    if (isSingapore) {
        return (
            <Col {...colSpan}>
                <FormFields.InputNumberField
                    {...t('moduleDetails:fields.coe', { returnObjects: true })}
                    disabled={disabled}
                    name={coeName}
                    required
                />
            </Col>
        );
    }

    if (isNewZealand) {
        return (
            <>
                <Col {...colSpan}>
                    <FormFields.InputNumberField
                        {...t('moduleDetails:fields.ppsr', { returnObjects: true })}
                        disabled={disabled}
                        name={ppsrName}
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <FormFields.InputNumberField
                        {...t('moduleDetails:fields.estFee', { returnObjects: true })}
                        disabled={disabled}
                        name={estFeeName}
                        required
                    />
                </Col>
                <BankEstFeeDisplayFields companyId={companyId} />
            </>
        );
    }

    return null;
};

export default MarketTypeField;
